import clsx from 'clsx';
import { useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Card, CardTitle } from '../../../../../shared/ui/card';
import {
  EDITOR_COLOR_MAP,
  EDITOR_CONTENT_STYLE,
  EDITOR_FONT_FAMILY_FORMATS,
  EDITOR_FONT_SIZE_FORMATS,
  EMAIL_EXTENDED_VALID_ELEMENTS,
  EMAIL_PLUGINS,
  EMAIL_TOOLBAR,
  SMS_CONTENT_MAX_LENGTH,
  SMS_EXTENDED_VALID_ELEMENTS,
  SMS_PLUGINS,
  SMS_TOOLBAR,
  TABLE_TOOLBAR,
} from './lib/editorConfig';
import { setupEditor } from './lib/setupEditor';
import { getTextLength } from './lib/editorHelpers';
import { CHANNEL } from '../../../constants/messageTemplateKeys';
import styles from './ContentEditorCard.module.css';

export function ContentEditorCard({
  className = '',
  channel = CHANNEL.EMAIL,
  onContentChange,
  onEditorInit,
  initialValue = '',
  customStyle = '',
}) {
  const isSms = channel === CHANNEL.SMS;
  const isPlainText = channel !== CHANNEL.EMAIL;
  const isEmail = channel === CHANNEL.EMAIL;
  // Append the template's own @media CSS so the editor renders multi-column layouts like the
  // saved email. Email only.
  const contentStyle =
    isEmail && customStyle ? `${EDITOR_CONTENT_STYLE}\n${customStyle}` : EDITOR_CONTENT_STYLE;
  const [smsLength, setSmsLength] = useState(() => getTextLength(initialValue));

  const handleEditorChange = (content) => {
    if (isSms) setSmsLength(getTextLength(content));
    onContentChange?.(content);
  };

  const isOverLimit = isSms && smsLength > SMS_CONTENT_MAX_LENGTH;

  return (
    <Card className={clsx(styles.card, className)} aria-label="Content Editor">
      <CardTitle>Content Editor</CardTitle>

      <div className={clsx(styles.editorStub)}>
        <Editor
          apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
          initialValue={initialValue}
          onEditorChange={handleEditorChange}
          // Report the normalized HTML TinyMCE actually holds — `initialValue` is not identical.
          onInit={(_event, editor) => onEditorInit?.(editor.getContent())}
          init={{
            height: '100%',
            // Store hrefs verbatim — otherwise TinyMCE rewrites absolute URLs
            // relative to its own document base, collapsing a pasted page URL
            // to just its last path segment.
            convert_urls: false,
            menubar: false,
            statusbar: false,
            branding: false,
            promotion: false,
            resize: false,
            plugins: isEmail ? EMAIL_PLUGINS : SMS_PLUGINS,
            // CTA buttons opt into inline boundaries so typing at the label edge edits
            // the label instead of leaking text beside it. Plain links stay out (their
            // boundary caret renders inside the last character in some browsers).
            inline_boundaries_selector: 'a[data-cta],code,.mce-annotation',
            toolbar: isEmail ? EMAIL_TOOLBAR : SMS_TOOLBAR,
            toolbar_mode: 'wrap',
            font_family_formats: EDITOR_FONT_FAMILY_FORMATS,
            font_size_formats: EDITOR_FONT_SIZE_FORMATS,
            color_map: EDITOR_COLOR_MAP,
            custom_colors: true,
            content_style: contentStyle,
            extended_valid_elements: isEmail
              ? EMAIL_EXTENDED_VALID_ELEMENTS
              : SMS_EXTENDED_VALID_ELEMENTS,
            table_toolbar: TABLE_TOOLBAR,
            setup: setupEditor(isPlainText),
          }}
        />
      </div>

      {isSms ? (
        <div className={clsx(styles.charCount)}>
          {isOverLimit && (
            <span className={clsx(styles.warningMessage, styles.charCountWarning)}>
              SMS content exceeds 160 characters. This message may be split into multiple SMS parts,
              which can result in additional charges.
            </span>
          )}
          <span className={clsx(isOverLimit && styles.charCountWarning)}>
            {smsLength}/{SMS_CONTENT_MAX_LENGTH}
          </span>
        </div>
      ) : null}
    </Card>
  );
}
